import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Layers, ArrowRight, Info, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog';
import {
  NOT_CONFIGURED,
  filterPlansForAgencyType,
  groupedPlans,
  normalizePlan,
  agencyTypeLabel,
  productTypeLabel,
  defaultPortalLabel,
  workspaceModeLabel,
  isPlanCompatibleWithAgencyType,
  getClassificationBadge,
} from '../../../utils/accountClassificationCatalog';
import AgencyClassificationBadge from '../../agency/AgencyClassificationBadge';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const LOAD_ERROR_MESSAGE =
  'Unable to load plan options. The configuration service is unavailable.';

const EMPTY_FORM = {
  agency_type: '',
  plan_id: '',
  reason: '',
};

const toFormState = (classification) => ({
  agency_type: classification?.agency_type || '',
  plan_id: classification?.plan_id || classification?.current_plan || '',
  reason: '',
});

/**
 * Normalize GET /classification payloads across slightly different response shapes.
 * Never throws — missing optional fields become null/empty.
 */
function normalizeClassificationResponse(data) {
  const payload = data && typeof data === 'object' ? data : {};
  const classification =
    payload.classification && typeof payload.classification === 'object'
      ? payload.classification
      : {
          agency_type: payload.agency_type ?? null,
          plan_id: payload.plan_id ?? payload.current_plan ?? null,
          current_plan: payload.current_plan ?? payload.plan_id ?? null,
          plan_name: payload.plan_name ?? null,
          plan_price: payload.plan_price ?? null,
          is_classified: Boolean(payload.agency_type || payload.plan_id || payload.current_plan),
        };

  const catalogRaw = payload.catalog || payload.plan_catalog || null;
  const catalog = catalogRaw && typeof catalogRaw === 'object'
    ? {
        agency_types: Array.isArray(catalogRaw.agency_types) ? catalogRaw.agency_types : [],
        plans: Array.isArray(catalogRaw.plans)
          ? catalogRaw.plans
          : Array.isArray(payload.compatible_plans)
            ? payload.compatible_plans
            : [],
      }
    : Array.isArray(payload.compatible_plans)
      ? { agency_types: [], plans: payload.compatible_plans }
      : null;

  return { classification, catalog };
}

/**
 * Account Configuration — platform-owner Agency Type + Current Plan controls.
 */
const AccountClassificationSection = ({
  agencyId,
  token,
  isPlatformAdmin,
  isReadOnly,
  onConfigurationSaved,
}) => {
  const canEdit = Boolean(isPlatformAdmin) && !isReadOnly;

  const tokenRef = useRef(token);
  const onSavedRef = useRef(onConfigurationSaved);
  const abortRef = useRef(null);
  const inFlightRef = useRef(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    onSavedRef.current = onConfigurationSaved;
  }, [onConfigurationSaved]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  const [clearedPlanMessage, setClearedPlanMessage] = useState('');

  const allPlans = useMemo(() => {
    // Only use plans from the successful GET catalog — never hardcode a fallback.
    const list = Array.isArray(catalog?.plans) ? catalog.plans : [];
    return list.map(normalizePlan);
  }, [catalog]);

  const agencyTypeOptions = useMemo(() => {
    if (catalog?.agency_types?.length) {
      return catalog.agency_types.map((item) => ({
        value: item.value,
        label: item.label,
      }));
    }
    return [];
  }, [catalog]);

  const compatiblePlans = useMemo(
    () => filterPlansForAgencyType(form.agency_type, allPlans),
    [form.agency_type, allPlans],
  );

  const planGroups = useMemo(() => groupedPlans(compatiblePlans), [compatiblePlans]);

  const selectedPlan = useMemo(
    () => compatiblePlans.find((p) => p.planId === form.plan_id) || null,
    [compatiblePlans, form.plan_id],
  );

  const loadClassification = useCallback(async () => {
    if (!agencyId) return;

    // Cancel any in-flight request (StrictMode remount, agency switch, Retry).
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeq.current;
    inFlightRef.current = true;

    console.debug('[AccountClassification] fetch:start', { agencyId });
    setLoading(true);
    setLoadError('');
    setClearedPlanMessage('');
    setLoaded(null);
    setCatalog(null);
    setForm({ ...EMPTY_FORM });

    const authToken = tokenRef.current;
    if (!authToken) {
      setLoadError(LOAD_ERROR_MESSAGE);
      setLoading(false);
      inFlightRef.current = false;
      console.debug('[AccountClassification] fetch:finish', { agencyId, reason: 'missing-token' });
      return;
    }

    try {
      const response = await axios.get(
        `${API}/admin/agencies/${agencyId}/classification`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: controller.signal,
        },
      );
      if (seq !== requestSeq.current || controller.signal.aborted) return;

      console.debug('[AccountClassification] fetch:success', response);
      const { classification, catalog: nextCatalog } = normalizeClassificationResponse(
        response?.data,
      );
      setLoaded(classification);
      setCatalog(nextCatalog);
      setForm(toFormState(classification));
      setReasonTouched(false);

      // Soft notify parent for badge/plan card — do not depend on this callback identity.
      const notify = onSavedRef.current;
      if (typeof notify === 'function' && classification) {
        notify(classification, { soft: true });
      }
    } catch (error) {
      if (
        seq !== requestSeq.current
        || controller.signal.aborted
        || axios.isCancel?.(error)
        || error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError'
        || error?.name === 'AbortError'
      ) {
        return;
      }
      console.debug('[AccountClassification] fetch:error', error);
      console.error('Failed to load account configuration:', error);
      setLoadError(LOAD_ERROR_MESSAGE);
      setLoaded(null);
      setCatalog(null);
      toast.error('Failed to load account configuration');
    } finally {
      // Only the active, non-aborted request owns the loading flag. Aborted
      // requests are superseded (or unmounting) and must not paint a blank form.
      if (seq === requestSeq.current && !controller.signal.aborted) {
        inFlightRef.current = false;
        setLoading(false);
        console.debug('[AccountClassification] fetch:finish', { agencyId });
      } else if (seq === requestSeq.current) {
        inFlightRef.current = false;
      }
    }
  }, [agencyId]);

  useEffect(() => {
    if (!agencyId) return undefined;
    loadClassification();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [agencyId, loadClassification]);

  const handleAgencyTypeChange = (nextType) => {
    setClearedPlanMessage('');
    setForm((prev) => {
      const next = { ...prev, agency_type: nextType };
      if (prev.plan_id) {
        const plan = allPlans.find((p) => p.planId === prev.plan_id);
        if (!isPlanCompatibleWithAgencyType(plan, nextType)) {
          next.plan_id = '';
          setClearedPlanMessage(
            'The previous plan was cleared because it is not available for this agency type.',
          );
        }
      }
      return next;
    });
  };

  const handlePlanChange = (planId) => {
    setForm((prev) => ({ ...prev, plan_id: planId }));
    setClearedPlanMessage('');
  };

  const handleReset = () => {
    setForm(toFormState(loaded));
    setReasonTouched(false);
    setClearedPlanMessage('');
  };

  const reasonValid = form.reason.trim().length > 0;
  const canSave =
    canEdit
    && Boolean(form.agency_type)
    && Boolean(form.plan_id)
    && reasonValid
    && !loading
    && !loadError
    && Boolean(selectedPlan);

  const planPlaceholder = !form.agency_type
    ? 'Select an agency type first'
    : loading
      ? 'Loading compatible plans...'
      : loadError
        ? 'Unable to load plan options'
        : compatiblePlans.length === 0
          ? 'No compatible plans available'
          : 'Select a plan...';

  const planDisabled =
    !canEdit
    || !form.agency_type
    || loading
    || Boolean(loadError)
    || compatiblePlans.length === 0;

  const changeRows = useMemo(() => {
    const nextPlan = selectedPlan;
    const currentPlan = allPlans.find((p) => p.planId === loaded?.plan_id);
    return [
      {
        key: 'agency_type',
        label: 'Agency type',
        current: agencyTypeLabel(loaded?.agency_type),
        next: agencyTypeLabel(form.agency_type),
      },
      {
        key: 'plan',
        label: 'Current plan',
        current: currentPlan
          ? `${currentPlan.name} ($${currentPlan.price}/mo)`
          : NOT_CONFIGURED,
        next: nextPlan ? `${nextPlan.name} ($${nextPlan.price}/mo)` : NOT_CONFIGURED,
      },
    ].filter((row) => row.current !== row.next);
  }, [loaded, form, selectedPlan, allPlans]);

  const handleOpenConfirm = () => {
    if (!canSave) {
      setReasonTouched(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (saving || !canSave) return;
    setSaving(true);
    try {
      const payload = {
        agency_type: form.agency_type,
        plan_id: form.plan_id,
        reason: form.reason.trim(),
      };
      const authToken = tokenRef.current;
      const res = await axios.patch(
        `${API}/admin/agencies/${agencyId}/classification`,
        payload,
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
      );
      toast.success('Account configuration updated');
      setConfirmOpen(false);
      const { classification } = normalizeClassificationResponse(res?.data);
      // Prefer PATCH body; fall back to a single explicit reload if body is empty.
      if (classification && (classification.agency_type || classification.plan_id)) {
        setLoaded(classification);
        setForm(toFormState(classification));
        setReasonTouched(false);
        setClearedPlanMessage('');
        const notify = onSavedRef.current;
        if (typeof notify === 'function') {
          notify(classification, { soft: false });
        }
      } else {
        await loadClassification();
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('fundelite:agency-classification-updated', {
            detail: { agencyId, classification },
          }),
        );
      }
    } catch (err) {
      console.error('Failed to save account configuration:', err);
      toast.error(err.response?.data?.detail || 'Failed to save account configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !loaded && !loadError) {
    return (
      <Card data-testid="account-classification-section">
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          Loading account configuration…
        </CardContent>
      </Card>
    );
  }

  if (loadError && !loaded) {
    return (
      <Card data-testid="account-classification-section">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Layers className="text-indigo-600" size={20} />
            </div>
            <div>
              <CardTitle className="text-lg">Account Configuration</CardTitle>
              <CardDescription>
                Define the agency type and current plan.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4" data-testid="classification-load-error">
          <p className="text-sm text-slate-600">{loadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadClassification}
            data-testid="classification-retry-btn"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const derivedBadge = form.agency_type
    ? getClassificationBadge({ agency_type: form.agency_type })
    : null;

  return (
    <Card data-testid="account-classification-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Layers className="text-indigo-600" size={20} />
            </div>
            <div>
              <CardTitle className="text-lg">Account Configuration</CardTitle>
              <CardDescription>
                Define the agency type and current plan. The selected plan determines the product,
                portal, workspace, entitlements, features, limits, and billing configuration.
              </CardDescription>
            </div>
          </div>
          <Badge
            className="bg-indigo-100 text-indigo-700 border-0 shrink-0"
            data-testid="platform-owner-only-badge"
          >
            <ShieldCheck size={12} className="mr-1" />
            Platform owner only
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {!canEdit && (
          <div
            className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"
            data-testid="classification-view-only"
          >
            <Info size={16} className="text-slate-400" />
            <p className="text-xs text-slate-500">
              You can view this agency&apos;s configuration, but only a platform owner can change it.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-2">
            <Label htmlFor="agency_type">Agency Type</Label>
            <Select
              value={form.agency_type || undefined}
              onValueChange={handleAgencyTypeChange}
              disabled={!canEdit || agencyTypeOptions.length === 0}
            >
              <SelectTrigger id="agency_type" data-testid="classification-agency_type">
                <SelectValue placeholder="Select an agency type..." />
              </SelectTrigger>
              <SelectContent>
                {agencyTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan_id">Current Plan</Label>
            <Select
              value={form.plan_id || undefined}
              onValueChange={handlePlanChange}
              disabled={planDisabled}
            >
              <SelectTrigger
                id="plan_id"
                data-testid="classification-plan_id"
                aria-describedby="plan-helper"
              >
                <SelectValue placeholder={planPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {planGroups.map((group) => (
                  <SelectGroup key={group.group}>
                    <SelectLabel>{group.group}</SelectLabel>
                    {group.plans.map((plan) => (
                      <SelectItem key={plan.planId} value={plan.planId}>
                        {plan.name} — ${plan.price}/month
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <p id="plan-helper" className="text-xs text-slate-500">
              {!form.agency_type
                ? 'Choose an agency type to view compatible plans.'
                : loadError
                  ? LOAD_ERROR_MESSAGE
                  : 'Only plans compatible with the selected agency type are shown.'}
            </p>
          </div>
        </div>

        {clearedPlanMessage ? (
          <p className="text-sm text-amber-700" role="status" aria-live="polite" data-testid="plan-cleared-message">
            {clearedPlanMessage}
          </p>
        ) : null}

        {selectedPlan ? (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm"
            data-testid="derived-plan-preview"
          >
            <div>
              <p className="text-xs text-slate-500">Monthly price</p>
              <p className="font-medium text-slate-900">${selectedPlan.price}/mo</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Primary product</p>
              <p className="font-medium text-slate-900">
                {productTypeLabel(selectedPlan.primaryProduct)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Default portal</p>
              <p className="font-medium text-slate-900">
                {defaultPortalLabel(selectedPlan.defaultPortal)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Workspace</p>
              <p className="font-medium text-slate-900">
                {workspaceModeLabel(selectedPlan.workspaceMode)}
              </p>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <p className="text-xs text-slate-500">Classification badge</p>
              <AgencyClassificationBadge
                agencyType={form.agency_type}
                classification={derivedBadge}
                testId="config-badge-preview"
              />
            </div>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="classification_reason">
            Reason for Change <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="classification_reason"
            data-testid="classification-reason"
            value={form.reason}
            disabled={!canEdit}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, reason: e.target.value }));
              setReasonTouched(true);
            }}
            placeholder="Explain why this configuration is changing…"
            className="min-h-[96px]"
          />
          {reasonTouched && !reasonValid && (
            <p className="text-xs text-red-600">Reason for change is required.</p>
          )}
        </div>

        <div className="flex flex-wrap gap-2" data-testid="classification-status-chips">
          <Badge variant="outline" data-testid="chip-agency-type">
            Agency type {form.agency_type ? 'configured' : 'not configured'}
          </Badge>
          <Badge variant="outline" data-testid="chip-plan">
            Plan {form.plan_id ? 'configured' : 'not configured'}
          </Badge>
          <Badge variant="outline">Agency ID preserved</Badge>
          <Badge variant="outline">Memberships preserved</Badge>
        </div>
      </CardContent>

      {canEdit && (
        <CardFooter className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={saving}
            data-testid="classification-reset-btn"
          >
            Reset
          </Button>
          <Button
            type="button"
            onClick={handleOpenConfirm}
            disabled={!canSave || saving}
            data-testid="classification-save-btn"
          >
            {saving ? 'Saving…' : 'Save Configuration'}
          </Button>
        </CardFooter>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="classification-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm configuration change</DialogTitle>
            <DialogDescription>
              Review the changes below. Agency ID and memberships are preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {changeRows.length === 0 ? (
              <p className="text-sm text-slate-500">No field changes detected.</p>
            ) : (
              changeRows.map((row) => (
                <div key={row.key} className="flex items-center gap-2 text-sm">
                  <span className="w-28 text-slate-500">{row.label}</span>
                  <span className="text-slate-700">{row.current}</span>
                  <ArrowRight size={14} className="text-slate-400" />
                  <span className="font-medium text-slate-900">{row.next}</span>
                </div>
              ))
            )}
            <p className="text-xs text-slate-500 pt-2">
              Reason: {form.reason.trim()}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={saving}
              data-testid="classification-confirm-save-btn"
            >
              {saving ? 'Saving…' : 'Confirm Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AccountClassificationSection;
